import type { StageSyncRequest } from "@/lib/api/model/stageSyncRequest";
import type { StageSyncCreate } from "@/lib/api/model/stageSyncCreate";
import type { StageSyncUpdate } from "@/lib/api/model/stageSyncUpdate";
import type { StageSyncDelete } from "@/lib/api/model/stageSyncDelete";
import type { StageMappingOverrideInSync } from "@/lib/api/model/stageMappingOverrideInSync";

/** Job Details fields tracked for dirty/validity gating. */
export interface DetailsSnapshot {
  title: string;
  description: string;
  managerId: string | null;
  employerId: string | null;
}

/**
 * Minimal stage shape used for baseline/diff comparison. Mirrors the mutable
 * fields of a pipeline stage; `ats_name` is intentionally excluded because it
 * is display-only and never part of dirtiness or the sync payload.
 */
export interface StageSnapshot {
  id: string;
  name: string;
  color: string;
  description: string;
  is_system: boolean;
}

/** Mapping draft/baseline: stage id → selected remote stage id (or null). */
export type MappingRecord = Record<string, string | null>;

/** Remote stage option needed to enrich `remote_stage_name` on overrides. */
export interface RemoteStageOption {
  remote_stage_id: string;
  name: string;
}

const CUSTOM_PREFIX = "custom-";

function isCustomId(id: string): boolean {
  return id.startsWith(CUSTOM_PREFIX);
}

/** True when any Details field differs from the baseline. */
export function detailsDirty(
  base: DetailsSnapshot,
  draft: DetailsSnapshot,
): boolean {
  return (
    base.title !== draft.title ||
    base.description !== draft.description ||
    base.managerId !== draft.managerId ||
    base.employerId !== draft.employerId
  );
}

/**
 * Details write gate: title ≥ 3 chars (trimmed), a manager, an employer, and a
 * non-empty (trimmed) description. Gates the Details PATCH only — never Stages.
 */
export function detailsValid(draft: DetailsSnapshot): boolean {
  return (
    draft.title.trim().length >= 3 &&
    !!draft.managerId &&
    !!draft.employerId &&
    (draft.description ?? "").trim().length > 0
  );
}

function stageFieldsChanged(a: StageSnapshot, b: StageSnapshot): boolean {
  return (
    a.name !== b.name ||
    a.color !== b.color ||
    (a.description ?? "") !== (b.description ?? "")
  );
}

/**
 * True when stages changed vs baseline: any name/color/description edit, add,
 * delete, or reorder. `ats_name` is ignored. System bookends are included in
 * the ordered-id comparison so a bookend position change still reads as dirty.
 */
export function stagesDirty(
  baseline: StageSnapshot[],
  localStages: StageSnapshot[],
): boolean {
  if (baseline.length !== localStages.length) return true;

  for (let i = 0; i < baseline.length; i++) {
    if (baseline[i].id !== localStages[i].id) return true;
  }

  const baseById = new Map(baseline.map((s) => [s.id, s]));
  for (const local of localStages) {
    const base = baseById.get(local.id);
    if (!base) return true;
    if (stageFieldsChanged(base, local)) return true;
  }

  return false;
}

function mappingValue(record: MappingRecord, key: string): string | null {
  return record[key] ?? null;
}

/**
 * True when any editable mapping entry differs from the baseline. When
 * `editableStageIds` is provided, only those stage ids are considered (system
 * bookends should be excluded by the caller).
 */
export function mappingDirty(
  baseline: MappingRecord,
  draft: MappingRecord,
  editableStageIds?: Iterable<string>,
): boolean {
  const keys = editableStageIds
    ? new Set(editableStageIds)
    : new Set<string>([...Object.keys(baseline), ...Object.keys(draft)]);

  for (const key of keys) {
    if (mappingValue(baseline, key) !== mappingValue(draft, key)) return true;
  }

  return false;
}

function orderedNonSystemIds(
  stages: StageSnapshot[],
  { excludeCustom }: { excludeCustom: boolean },
): string[] {
  return stages
    .filter((s) => !s.is_system && !(excludeCustom && isCustomId(s.id)))
    .map((s) => s.id);
}

function sameOrder(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export interface BuildStageSyncPayloadArgs {
  stagesBaseline: StageSnapshot[];
  localStages: StageSnapshot[];
  mappingBaseline: MappingRecord;
  mappingDraft: MappingRecord;
  remoteStages: RemoteStageOption[];
  atsLinked: boolean;
}

/**
 * Build a diff-only `StageSyncRequest` body from baselines vs current drafts.
 *
 * - `updates`: real-UUID stages whose name/color/description changed.
 * - `creates`: `custom-*` stages (non-ATS only), keyed by their id as temp_id.
 * - `deletes`: baseline non-system ids missing from current (non-ATS only).
 * - `order`: required when creates/deletes present; otherwise only when the
 *   editable order differs. Lists all and only non-system stages in the
 *   projected final set (real UUIDs and create temp_ids).
 * - `mapping_overrides`: only entries differing from baseline, excluding system
 *   bookends, enriched with `remote_stage_name`.
 *
 * Empty arrays and undefined keys are omitted for a clean payload.
 */
export function buildStageSyncPayload({
  stagesBaseline,
  localStages,
  mappingBaseline,
  mappingDraft,
  remoteStages,
  atsLinked,
}: BuildStageSyncPayloadArgs): StageSyncRequest {
  const baseById = new Map(stagesBaseline.map((s) => [s.id, s]));
  const localById = new Map(localStages.map((s) => [s.id, s]));

  const updates: StageSyncUpdate[] = [];
  const creates: StageSyncCreate[] = [];

  for (const stage of localStages) {
    if (stage.is_system) continue;

    if (isCustomId(stage.id)) {
      // ATS-linked jobs cannot create stages in Settings.
      if (atsLinked) continue;
      creates.push({
        temp_id: stage.id,
        name: stage.name,
        color: stage.color,
        description: stage.description ?? "",
      });
      continue;
    }

    const base = baseById.get(stage.id);
    if (!base) continue;
    if (stageFieldsChanged(base, stage)) {
      updates.push({
        id: stage.id,
        name: stage.name,
        color: stage.color,
        description: stage.description ?? "",
      });
    }
  }

  const deletes: StageSyncDelete[] = [];
  if (!atsLinked) {
    for (const base of stagesBaseline) {
      if (base.is_system) continue;
      if (!localById.has(base.id)) deletes.push({ id: base.id });
    }
  }

  const projectedOrder = orderedNonSystemIds(localStages, {
    excludeCustom: atsLinked,
  });
  const baselineOrder = orderedNonSystemIds(stagesBaseline, {
    excludeCustom: atsLinked,
  });

  let order: string[] | undefined;
  if (creates.length > 0 || deletes.length > 0) {
    order = projectedOrder;
  } else if (!sameOrder(projectedOrder, baselineOrder)) {
    order = projectedOrder;
  }

  const systemStageIds = new Set<string>();
  for (const s of stagesBaseline) if (s.is_system) systemStageIds.add(s.id);
  for (const s of localStages) if (s.is_system) systemStageIds.add(s.id);

  const mappingKeys: string[] = [];
  const seenKeys = new Set<string>();
  for (const key of [
    ...Object.keys(mappingDraft),
    ...Object.keys(mappingBaseline),
  ]) {
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);
    mappingKeys.push(key);
  }

  const mappingOverrides: StageMappingOverrideInSync[] = [];
  for (const stageId of mappingKeys) {
    if (systemStageIds.has(stageId)) continue;
    const draftValue = mappingValue(mappingDraft, stageId);
    const baseValue = mappingValue(mappingBaseline, stageId);
    if (draftValue === baseValue) continue;

    const remoteStageName =
      draftValue == null
        ? null
        : (remoteStages.find((r) => r.remote_stage_id === draftValue)?.name ??
          null);

    mappingOverrides.push({
      stage_id: stageId,
      remote_stage_id: draftValue,
      remote_stage_name: remoteStageName,
    });
  }

  const body: StageSyncRequest = {};
  if (creates.length > 0) body.creates = creates;
  if (updates.length > 0) body.updates = updates;
  if (deletes.length > 0) body.deletes = deletes;
  if (order !== undefined) body.order = order;
  if (mappingOverrides.length > 0) body.mapping_overrides = mappingOverrides;

  return body;
}
